/**
 * Tracking & Attribution System
 * UTM parameters, CTA tracking, device detection - All cookieless via sessionStorage
 */

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

/**
 * Initialize all tracking on page load
 * This runs ALWAYS, even without cookie consent
 */
export function initializeTracking(): void {
  if (typeof window === 'undefined') return;
  
  // Capture UTM parameters from URL
  captureUTMParameters();
  
  // Store first landing URL
  storeFirstLandingURL();
  
  // Extract search query from referrer
  captureSearchQuery();
  
  // Setup CTA click listener
  setupCTAListener();
}

/**
 * Capture UTM parameters from URL and store in sessionStorage
 * Tracks both first-touch and last-touch attribution
 */
export function captureUTMParameters(): void {
  const urlParams = new URLSearchParams(window.location.search);
  
  // Check if any UTM parameters exist
  const hasUTM = urlParams.has('utm_source') || 
                 urlParams.has('utm_medium') || 
                 urlParams.has('utm_campaign');
  
  if (!hasUTM) return;
  
  // First-touch: Only set if not already set (first visit with UTM)
  if (!sessionStorage.getItem('utm_source_first')) {
    sessionStorage.setItem('utm_source_first', urlParams.get('utm_source') || '');
    sessionStorage.setItem('utm_medium_first', urlParams.get('utm_medium') || '');
    sessionStorage.setItem('utm_campaign_first', urlParams.get('utm_campaign') || '');
    sessionStorage.setItem('utm_term_first', urlParams.get('utm_term') || '');
    sessionStorage.setItem('utm_content_first', urlParams.get('utm_content') || '');
    sessionStorage.setItem('utm_id_first', urlParams.get('utm_id') || '');
  }
  
  // Last-touch: Always update (most recent visit with UTM)
  sessionStorage.setItem('utm_source_last', urlParams.get('utm_source') || '');
  sessionStorage.setItem('utm_medium_last', urlParams.get('utm_medium') || '');
  sessionStorage.setItem('utm_campaign_last', urlParams.get('utm_campaign') || '');
  sessionStorage.setItem('utm_term_last', urlParams.get('utm_term') || '');
  sessionStorage.setItem('utm_content_last', urlParams.get('utm_content') || '');
  sessionStorage.setItem('utm_id_last', urlParams.get('utm_id') || '');
  
  // Clean URL (remove UTM parameters from address bar)
  cleanURL();
}

/**
 * Remove UTM parameters from URL for clean SEO-friendly URLs
 */
function cleanURL(): void {
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);
  
  // Remove all UTM parameters
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'].forEach(param => {
    params.delete(param);
  });
  
  // Update URL without reload
  const newURL = params.toString() 
    ? `${url.pathname}?${params.toString()}`
    : url.pathname;
  
  window.history.replaceState({}, document.title, newURL);
}

/**
 * Store the first landing URL (entry point to website)
 */
function storeFirstLandingURL(): void {
  if (!sessionStorage.getItem('first_landing_url')) {
    const landingUrl = window.location.href;
    sessionStorage.setItem('first_landing_url', landingUrl);
    
    // Also extract and store the page path for cleaner display
    try {
      const url = new URL(landingUrl);
      sessionStorage.setItem('first_landing_page', url.pathname);
    } catch (e) {
      sessionStorage.setItem('first_landing_page', landingUrl);
    }
  }
}

/**
 * Extract search query from Google/Bing referrer
 * Captures organic search keywords when user comes from search engines
 */
function captureSearchQuery(): void {
  const referrer = document.referrer;
  
  if (!referrer) return;
  
  try {
    const referrerUrl = new URL(referrer);
    const hostname = referrerUrl.hostname.toLowerCase();
    
    // Google Search
    if (hostname.includes('google.')) {
      const query = referrerUrl.searchParams.get('q');
      if (query) {
        sessionStorage.setItem('search_query', query);
        sessionStorage.setItem('search_engine', 'Google');
      }
    }
    // Bing Search
    else if (hostname.includes('bing.')) {
      const query = referrerUrl.searchParams.get('q');
      if (query) {
        sessionStorage.setItem('search_query', query);
        sessionStorage.setItem('search_engine', 'Bing');
      }
    }
    // DuckDuckGo
    else if (hostname.includes('duckduckgo.')) {
      const query = referrerUrl.searchParams.get('q');
      if (query) {
        sessionStorage.setItem('search_query', query);
        sessionStorage.setItem('search_engine', 'DuckDuckGo');
      }
    }
    // Yahoo
    else if (hostname.includes('yahoo.')) {
      const query = referrerUrl.searchParams.get('p');
      if (query) {
        sessionStorage.setItem('search_query', query);
        sessionStorage.setItem('search_engine', 'Yahoo');
      }
    }
  } catch (e) {
    // Invalid URL, ignore
  }
}

/**
 * Setup global CTA click tracking listener
 * Tracks any element with data-cta-id attribute
 */
export function setupCTAListener(): void {
  document.addEventListener('click', (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const ctaElement = target.closest('[data-cta-id]') as HTMLElement;
    
    if (!ctaElement) return;
    
    // Get CTA data attributes
    const ctaId = ctaElement.getAttribute('data-cta-id');
    const ctaPosition = ctaElement.getAttribute('data-cta-position');
    const ctaVariant = ctaElement.getAttribute('data-cta-variant');
    
    // Store in sessionStorage (cookieless!)
    if (ctaId) sessionStorage.setItem('cta_id', ctaId);
    if (ctaPosition) sessionStorage.setItem('cta_position', ctaPosition);
    if (ctaVariant) sessionStorage.setItem('cta_variant', ctaVariant);
    
    // Push to dataLayer for GTM (also cookieless)
    pushToDataLayer({
      event: 'cta_click',
      cta_id: ctaId,
      cta_position: ctaPosition,
      cta_variant: ctaVariant,
    });
  });
}

/**
 * Detect device category based on screen width
 */
export function detectDeviceCategory(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  
  const width = window.innerWidth;
  
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

/**
 * Push event to GTM dataLayer
 */
export function pushToDataLayer(data: Record<string, any>): void {
  if (typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
}

/**
 * Get all tracking data from sessionStorage
 * Used when submitting forms to HubSpot
 */
export function getAllTrackingData(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  
  return {
    // UTM First-touch
    utm_source_first: sessionStorage.getItem('utm_source_first') || '',
    utm_medium_first: sessionStorage.getItem('utm_medium_first') || '',
    utm_campaign_first: sessionStorage.getItem('utm_campaign_first') || '',
    utm_term_first: sessionStorage.getItem('utm_term_first') || '',
    utm_content_first: sessionStorage.getItem('utm_content_first') || '',
    utm_id_first: sessionStorage.getItem('utm_id_first') || '',
    
    // UTM Last-touch
    utm_source_last: sessionStorage.getItem('utm_source_last') || '',
    utm_medium_last: sessionStorage.getItem('utm_medium_last') || '',
    utm_campaign_last: sessionStorage.getItem('utm_campaign_last') || '',
    utm_term_last: sessionStorage.getItem('utm_term_last') || '',
    utm_content_last: sessionStorage.getItem('utm_content_last') || '',
    utm_id_last: sessionStorage.getItem('utm_id_last') || '',
    
    // CTA Attribution
    cta_id: sessionStorage.getItem('cta_id') || '',
    cta_position: sessionStorage.getItem('cta_position') || '',
    cta_variant: sessionStorage.getItem('cta_variant') || '',
    
    // Organic Search & AI Tracking
    search_query: sessionStorage.getItem('search_query') || '',
    search_engine: sessionStorage.getItem('search_engine') || '',
    traffic_source_type: sessionStorage.getItem('traffic_source_type') || '',
    
    // Session data
    first_landing_url: sessionStorage.getItem('first_landing_url') || '',
    first_landing_page: sessionStorage.getItem('first_landing_page') || '',
    origin_url: window.location.href,
    referrer_url: document.referrer || '',
    
    // Device & context
    device_category: detectDeviceCategory(),
    browser_language: navigator.language,
    page_language: document.documentElement.lang || 'en',
    page_locale: getPageLocale(),

    // Device details (requires analytics consent — only included in this consent-gated function)
    ...detectDevice(),
  };
}

/**
 * Detect device name, browser and OS from userAgent.
 * Only called with analytics consent.
 */
export function detectDevice(): { device_name: string; browser_name: string; os_name: string } {
  if (typeof window === 'undefined') return { device_name: '', browser_name: '', os_name: '' };

  const ua = navigator.userAgent;

  // Device name
  let device_name = 'Unknown';
  if (/iPhone/.test(ua)) device_name = 'iPhone';
  else if (/iPad/.test(ua)) device_name = 'iPad';
  else if (/Android/.test(ua)) {
    const match = ua.match(/;\s([^;)]+)\sBuild/);
    device_name = match ? match[1].trim() : 'Android';
  }
  else if (/Macintosh/.test(ua)) device_name = 'Mac';
  else if (/Windows/.test(ua)) device_name = 'Windows PC';
  else if (/Linux/.test(ua)) device_name = 'Linux';

  // Browser name (order matters: Edge/Opera before Chrome)
  let browser_name = 'Unknown';
  if (/Edg\//.test(ua)) browser_name = 'Edge';
  else if (/OPR\/|Opera/.test(ua)) browser_name = 'Opera';
  else if (/Chrome\//.test(ua)) browser_name = 'Chrome';
  else if (/Firefox\//.test(ua)) browser_name = 'Firefox';
  else if (/Safari\//.test(ua)) browser_name = 'Safari';

  // OS name + version
  let os_name = 'Unknown';
  const iosMatch = ua.match(/CPU (?:iPhone )?OS ([\d_]+)/);
  const androidMatch = ua.match(/Android ([\d.]+)/);
  const macMatch = ua.match(/Mac OS X ([\d_]+)/);
  const windowsMatch = ua.match(/Windows NT ([\d.]+)/);

  if (iosMatch) {
    const v = iosMatch[1].replace(/_/g, '.');
    os_name = /iPad/.test(ua) ? `iPadOS ${v}` : `iOS ${v}`;
  } else if (androidMatch) {
    os_name = `Android ${androidMatch[1]}`;
  } else if (macMatch) {
    os_name = `macOS ${macMatch[1].replace(/_/g, '.')}`;
  } else if (windowsMatch) {
    const winVersions: Record<string, string> = {
      '10.0': 'Windows 10/11', '6.3': 'Windows 8.1',
      '6.2': 'Windows 8', '6.1': 'Windows 7',
    };
    os_name = winVersions[windowsMatch[1]] || `Windows NT ${windowsMatch[1]}`;
  }

  return { device_name, browser_name, os_name };
}

/**
 * Get visitor's timezone (cookieless, no consent needed).
 * Example: "Europe/Amsterdam"
 */
export function getTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

/**
 * Track the current page visit in sessionStorage.
 * Called by PageTracker on every route change.
 * Cookieless — no consent needed.
 */
export function trackPageVisit(): void {
  if (typeof window === 'undefined') return;

  const pathname = window.location.pathname;
  const timestamp = Date.now();

  try {
    const existing: Array<{ path: string; time: number }> =
      JSON.parse(sessionStorage.getItem('page_visits') || '[]');

    // Skip duplicate consecutive pages
    if (existing.length > 0 && existing[existing.length - 1].path === pathname) return;

    existing.push({ path: pathname, time: timestamp });

    // Keep max 20 pages to avoid bloat
    if (existing.length > 20) existing.shift();

    sessionStorage.setItem('page_visits', JSON.stringify(existing));
  } catch {
    // sessionStorage unavailable — fail silently
  }
}

/**
 * Format page visits for HubSpot / email.
 * Example: "/nl/kiosk (2m14s) → /nl/qr-bestellen (0m47s) → /nl/demo-request"
 * Cookieless — no consent needed to SEND (data was stored without consent).
 */
export function getPagesVisited(): string {
  if (typeof window === 'undefined') return '';

  try {
    const visits: Array<{ path: string; time: number }> =
      JSON.parse(sessionStorage.getItem('page_visits') || '[]');

    if (visits.length === 0) return '';

    return visits
      .map((v, i) => {
        if (i === visits.length - 1) return v.path;
        const secs = Math.round((visits[i + 1].time - v.time) / 1000);
        const dur = secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m${secs % 60 > 0 ? secs % 60 + 's' : ''}`;
        return `${v.path} (${dur})`;
      })
      .join(' → ');
  } catch {
    return '';
  }
}

/**
 * Get page locale from URL pathname
 */
function getPageLocale(): string {
  const pathname = window.location.pathname;
  const parts = pathname.split('/').filter(Boolean);
  
  // Check if first part is a locale (nl, de)
  if (parts[0] === 'nl' || parts[0] === 'de') {
    return parts[0];
  }
  
  // Default to 'en' if no locale prefix
  return 'en';
}

